export const PATIENTS_ROUTE_PATH = "/app/patients";
export const INBOX_ROUTE_PATH = "/app/inbox";
export const CALENDAR_ROUTE_PATH = "/app/calendar";

export interface InboxConversationPatientReference {
    patientId?: string | null;
}

export interface InboxNavigationState {
    prefilledMessage?: string;
}

export interface PatientReferenceMatch {
    id: string;
    userId?: string | null;
}

export type PatientProfileSelectionSource = "highlight" | "patient" | "id";

export interface PatientProfileSelection {
    patientId: string | null;
    source: PatientProfileSelectionSource | null;
    tab: string | null;
}

export function buildPatientProfilePath(
    patientId: string,
    options?: { tab?: string },
): string {
    const params = new URLSearchParams();
    params.set("highlight", patientId);

    if (options?.tab) {
        params.set("tab", options.tab);
    }

    return `${PATIENTS_ROUTE_PATH}?${params.toString()}`;
}

export function buildPatientDocumentsPath(patientId: string): string {
    return buildPatientProfilePath(patientId, { tab: "documents" });
}

export function buildPatientInboxPath(patientId: string): string {
    const params = new URLSearchParams();
    params.set("patient", patientId);

    return `${INBOX_ROUTE_PATH}?${params.toString()}`;
}

export function findConversationByPatientReference<T extends InboxConversationPatientReference>(
    conversations: readonly T[],
    patientIdOrUserId: string,
    patient?: PatientReferenceMatch | null,
): T | undefined {
    const candidateIds = new Set(
        [patientIdOrUserId, patient?.id, patient?.userId].filter(
            (value): value is string => Boolean(value),
        ),
    );

    return conversations.find(
        (conversation) =>
            typeof conversation.patientId === "string" && candidateIds.has(conversation.patientId),
    );
}

export function buildPatientAppointmentCreatePath(patientId: string): string {
    const params = new URLSearchParams();
    params.set("action", "create");
    params.set("patientId", patientId);

    return `${CALENDAR_ROUTE_PATH}?${params.toString()}`;
}

export function buildCalendarAppointmentDetailPath(appointmentId: string): string {
    const params = new URLSearchParams();
    params.set("appointmentId", appointmentId);

    return `${CALENDAR_ROUTE_PATH}?${params.toString()}`;
}

export function getPatientProfileSelection(
    searchParams: URLSearchParams,
): PatientProfileSelection {
    const sources: PatientProfileSelectionSource[] = ["highlight", "patient", "id"];

    for (const source of sources) {
        const patientId = searchParams.get(source);
        if (patientId) {
            return {
                patientId,
                source,
                tab: searchParams.get("tab"),
            };
        }
    }

    return {
        patientId: null,
        source: null,
        tab: searchParams.get("tab"),
    };
}